<template>
  <div>
    <h2>Email senden</h2>

    <div class="mb-3">
      <label class="form-label">Empfänger</label>
      <div class="mb-2">
        <button class="btn btn-sm btn-outline-primary me-2" @click="bulkAdd(false)">Alle hinzufügen</button>
        <button class="btn btn-sm btn-outline-primary me-2" @click="bulkAdd(true)">Aktive hinzufügen</button>
        <button class="btn btn-sm btn-outline-secondary" @click="bulkRemoveAll">Alle entfernen</button>
      </div>
      <multiselect v-model="selectedRecipientIds" :options="altarServerStore.getIDs"
                   :close-on-select="false" :multiple="true" :allow-empty="true"
                   :custom-label="id => altarServerStore.getPlannameById(id)"
                   placeholder="Empfänger auswählen..." class="form-control">
      </multiselect>
    </div>

    <div class="mb-3">
      <label for="email-subject" class="form-label">Betreff</label>
      <input id="email-subject" v-model="subject" type="text" class="form-control" placeholder="Betreff eingeben">
    </div>

    <div class="mb-3">
      <label for="email-message" class="form-label">Nachricht</label>
      <TipTapEditor v-model="message" id="email-message" class="mb-3"></TipTapEditor>
    </div>

    <email-template-selector
      :subject="subject"
      :message="message"
      @update:subject="subject = $event"
      @update:message="message = $event"
      @toast="result = { ok: $event.ok, message: $event.message }"
    ></email-template-selector>

    <div class="mb-3">
      <label for="email-schedule" class="form-label">Messdienerplan anhängen (optional)</label>
      <select id="email-schedule" v-model="scheduleId" class="form-select">
        <option :value="null">-- Kein Plan --</option>
        <option v-for="schedule in this.scheduleStore.sortedData" :key="schedule.id" :value="schedule.id">
          {{ formatScheduleName(schedule) }}
        </option>
      </select>
    </div>

    <div v-if="!previewMode" class="mb-3">
      <button class="btn btn-secondary" :disabled="!subject || !message" @click="preview">
        <em class="align-middle material-icons">visibility</em> Vorschau
      </button>
    </div>

    <div v-if="previewMode" class="card mb-3">
      <div class="card-header">Vorschau</div>
      <div class="card-body">
        <p><strong>Betreff:</strong> {{ subject }}</p>
        <p><strong>Nachricht:</strong></p>
        <div class="border p-2 bg-light" v-html="message"></div>
        <p><strong>Empfänger:</strong> {{ recipients.length }} Messdiener</p>
      </div>
    </div>

    <div v-if="previewMode" class="mb-3">
      <button class="btn btn-success" :disabled="sending" @click="send">
        <em v-if="!sending" class="align-middle material-icons">send</em>
        <span v-else class="spinner-border spinner-border-sm"></span>
        {{ sending ? 'Sende...' : 'Email senden' }}
      </button>
      <button class="btn btn-secondary ms-2" @click="reset">Zurück</button>
    </div>

    <div v-if="result" class="alert" :class="result.ok ? 'alert-success' : 'alert-danger'">
      {{ result.message }}
    </div>
  </div>
</template>

<script>
import { useScheduleStore } from '@/stores/schedule';
import { useEmailTemplateStore } from '@/stores/emailTemplate';
import { useAltarServerStore } from '@/stores/altarServer';
import Multiselect from "vue-multiselect";
import EmailTemplateSelector from '@/components/EmailTemplateSelector';
import TipTapEditor from '@/components/TipTapEditor';

export default {
  name: 'EmailSend',
  components: { EmailTemplateSelector, Multiselect, TipTapEditor },
  data() {
    return {
      subject: '',
      message: '',
      selectedRecipientIds: [],
      recipients: [],
      previewMode: false,
      sending: false,
      result: null,
      scheduleId: null
    };
  },
  setup() {
    return { scheduleStore: useScheduleStore(), templateStore: useEmailTemplateStore(), altarServerStore: useAltarServerStore() };
  },
  mounted() {
    if (!this.scheduleStore.loadedData) {
      this.scheduleStore.getData();
    }
    if (!this.templateStore.loadedData) {
      this.templateStore.getData();
    }
    if (!this.altarServerStore.loadedData) {
      this.altarServerStore.getData();
    }
  },
  methods: {
    bulkAdd(activeOnly) {
      const sources = activeOnly
        ? this.altarServerStore.data.filter(a => a.active).map(a => a.id)
        : this.altarServerStore.getIDs;
      this.selectedRecipientIds = [...new Set([...this.selectedRecipientIds, ...sources])];
    },
    bulkRemoveAll() {
      this.selectedRecipientIds = [];
    },
    async preview() {
      try {
        const resp = await this.axios.post('/email/preview/', {
          recipient_ids: this.selectedRecipientIds
        });
        this.recipients = resp.data.recipients;
        this.previewMode = true;
        this.result = null;
      } catch (e) {
        this.result = { ok: false, message: 'Fehler beim Laden der Vorschau.' };
      }
    },
    async send() {
      this.sending = true;
      this.result = null;
      try {
        const resp = await this.axios.post('/email/send/', {
          subject: this.subject,
          message: this.message,
          recipient_ids: this.selectedRecipientIds,
          schedule_id: this.scheduleId
        });
        this.result = { ok: true, message: resp.data.detail };
        this.reset();
      } catch (e) {
        this.result = {
          ok: false,
          message: e.response?.data?.detail || 'Fehler beim Senden.'
        };
      } finally {
        this.sending = false;
      }
    },
    reset() {
      this.subject = '';
      this.message = '';
      this.selectedRecipientIds = [];
      this.recipients = [];
      this.previewMode = false;
      this.result = null;
      this.scheduleId = null;
    },
    formatScheduleName(schedule) {
      const fmt = (d) => d.replaceAll('-', '_');
      return `${fmt(schedule.startDate)}-${fmt(schedule.endDate)}_Messdienerplan.pdf`;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>